// TEMPORARY MOCK ADAPTER for PRO Deployment Management (PRD 06).
//
// In-memory state lives in the module-level `items` / `modules` arrays and
// resets on hard reload. Mutations resolve with ~300 ms simulated latency.
// Errors mimic the backend envelope (ProDeploymentApiError).
//
// Deploy history persists to sessionStorage under `mcDeployHistory` (matches
// the prototype + the open question Q7 default in
// docs/api-contracts/pro-deployment-management.md).
//
// Seed values are ported verbatim from prototype/pages/module-config.html.
// Once PRO Item Design (PRD 04) ships in React, `items` should be sourced from
// that feature's adapter instead — see TODO(pro-item-design).
//
// Backend switch: VITE_USE_MOCK_API=false → swaps to pro-deployment.api.ts.

import {
  ProDeploymentApiError,
  type DeployHistoryEntry,
  type DeployInput,
  type DeployResult,
  type DeployState,
  type DiseaseFilterOption,
  type Frequency,
  type ItemPoolQuery,
  type Module,
  type ProDeploymentApi,
  type ProDeploymentCurrentConfig,
  type ProItemRef,
  type UpdateModuleInput,
  type UpdatePageConfigInput,
} from '../types/pro-deployment.types';

const LATENCY_MS = 300;

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

const HISTORY_KEY = 'mcDeployHistory';
const HISTORY_CAP = 50;

// ── Seed: PRO item pool (prototype `allItems`) ───────────────────────────────
//
// Prototype uses string ids like 'med-yn'; we promote these to formal PIDs
// (P-####) here so they match the rest of the platform. Display order matches
// the prototype.
interface SeedItem {
  pid: string;
  legacyId: string;
  name: string;
  questionCount: number;
  required: boolean;
  freqCat: Frequency;
  area: string;
}

const seedItems: SeedItem[] = [
  // 매일
  { pid: 'P-0001', legacyId: 'med-yn',      name: '복약 여부',            questionCount: 1,  required: true,  freqCat: 'daily',     area: '정서·인지 증상 관리' },
  { pid: 'P-0002', legacyId: 'side-effect', name: '부작용 유무',          questionCount: 1,  required: true,  freqCat: 'daily',     area: '정서·인지 증상 관리' },
  { pid: 'P-0003', legacyId: 'memo',        name: '특이사항 메모',        questionCount: 1,  required: false, freqCat: 'daily',     area: '사회·환경적 지원' },
  // 매주
  { pid: 'P-0004', legacyId: 'depression',     name: '지속적인 우울감',     questionCount: 1,  required: true,  freqCat: 'weekly',    area: '정서·인지 증상 관리' },
  { pid: 'P-0005', legacyId: 'meal-regular',   name: '규칙적인 식사',       questionCount: 1,  required: true,  freqCat: 'weekly',    area: '신체·생활습관 요인 관리' },
  { pid: 'P-0006', legacyId: 'role-difficulty', name: '역할 수행 어려움',    questionCount: 9,  required: true,  freqCat: 'weekly',    area: '신체·생활습관 요인 관리' },
  // 격주
  { pid: 'P-0007', legacyId: 'insomnia',     name: '불면·과수면',         questionCount: 7,  required: true,  freqCat: 'biweekly',  area: '정서·인지 증상 관리' },
  { pid: 'P-0008', legacyId: 'sleep-record', name: '수면 자기기록',       questionCount: 5,  required: true,  freqCat: 'biweekly',  area: '사회·환경적 지원' },
  // 매월
  { pid: 'P-0009', legacyId: 'suicide-risk',      name: '자살 관련 행동',           questionCount: 5,  required: true,  freqCat: 'monthly',   area: '정서·인지 증상 관리' },
  { pid: 'P-0010', legacyId: 'activity-worse',    name: '신체활동 악화',           questionCount: 3,  required: true,  freqCat: 'monthly',   area: '신체·생활습관 요인 관리' },
  { pid: 'P-0011', legacyId: 'high-risk-drink',   name: '고위험 음주',             questionCount: 4,  required: true,  freqCat: 'monthly',   area: '신체·생활습관 요인 관리' },
  { pid: 'P-0012', legacyId: 'emotional-support', name: '정서적 지지망',           questionCount: 3,  required: false, freqCat: 'monthly',   area: '신체·생활습관 요인 관리' },
  { pid: 'P-0013', legacyId: 'medical-use',       name: '일차의료기관 이용',       questionCount: 2,  required: true,  freqCat: 'monthly',   area: '사회·환경적 지원' },
  { pid: 'P-0014', legacyId: 'emergency-call',    name: '위기 시 비상 연락',       questionCount: 2,  required: true,  freqCat: 'monthly',   area: '사회·환경적 지원' },
  { pid: 'P-0015', legacyId: 'welfare-link',      name: '정신건강복지센터 연계',   questionCount: 2,  required: true,  freqCat: 'monthly',   area: '사회·환경적 지원' },
  { pid: 'P-0016', legacyId: 'resource-network',  name: '자원 네트워크',           questionCount: 2,  required: false, freqCat: 'monthly',   area: '사회·환경적 지원' },
  { pid: 'P-0017', legacyId: 'emergency-coping',  name: '응급 상황 대처',          questionCount: 3,  required: true,  freqCat: 'monthly',   area: '자기관리 및 가족·자원 연계' },
  { pid: 'P-0018', legacyId: 'caregiver-exists',  name: '주 돌봄자 유무',          questionCount: 1,  required: false, freqCat: 'monthly',   area: '자기관리 및 가족·자원 연계' },
  { pid: 'P-0019', legacyId: 'self-efficacy',     name: '일상관리 지속 의지',      questionCount: 21, required: true,  freqCat: 'monthly',   area: '자기관리 및 가족·자원 연계' },
  // 분기
  { pid: 'P-0020', legacyId: 'caregiver-understanding', name: '보호자 질병 이해도', questionCount: 3, required: true, freqCat: 'quarterly', area: '자기관리 및 가족·자원 연계' },
];

// Scope assignment matches prototype lines 212–233.
const groupItems = new Set([
  'depression',
  'meal-regular',
  'role-difficulty',
  'insomnia',
  'sleep-record',
  'activity-worse',
  'high-risk-drink',
  'emotional-support',
  'self-efficacy',
  'caregiver-understanding',
]);

const itemPool: ProItemRef[] = seedItems.map((s) => {
  const isGroup = groupItems.has(s.legacyId);
  const isSpecific = s.legacyId === 'suicide-risk';
  return {
    pid: s.pid,
    name: s.name,
    questionCount: s.questionCount,
    required: s.required,
    freqCat: s.freqCat,
    area: s.area,
    scope: isSpecific ? 'SPECIFIC' : isGroup ? 'GROUP' : 'COMMON',
    diseaseGroup: isSpecific || isGroup ? '정신건강' : '',
    diseaseCode: isSpecific ? 'F32' : '',
  };
});

// ── Seed: modules (prototype `modules`) ─────────────────────────────────────
// Use the seed legacy ids to resolve PIDs for initial composition.
function pidByLegacy(legacyId: string): string {
  const found = seedItems.find((s) => s.legacyId === legacyId);
  if (!found) throw new Error(`Mock seed error: unknown legacy id "${legacyId}"`);
  return found.pid;
}

const modules: Module[] = [
  {
    mid: 'M-0001',
    name: '매일 모듈',
    icon: '📋',
    frequency: 'daily',
    weekdays: [],
    frequencyLabel: '매일',
    responseLabel: '매일 필수 응답',
    responseLabelType: 'warning',
    itemIds: ['med-yn', 'side-effect', 'memo'].map(pidByLegacy),
    areaOrder: [],
  },
  {
    mid: 'M-0002',
    name: '매주(월) 모듈',
    icon: '📋',
    frequency: 'weekly',
    weekdays: ['MON'],
    frequencyLabel: '매주 월요일',
    responseLabel: '해당 주 내 자유 응답',
    responseLabelType: 'primary',
    itemIds: ['depression', 'meal-regular'].map(pidByLegacy),
    areaOrder: [],
  },
  {
    mid: 'M-0003',
    name: '매주(화) 모듈',
    icon: '📋',
    frequency: 'weekly',
    weekdays: ['TUE'],
    frequencyLabel: '매주 화요일',
    responseLabel: '해당 주 내 자유 응답',
    responseLabelType: 'primary',
    itemIds: ['role-difficulty'].map(pidByLegacy),
    areaOrder: [],
  },
  {
    mid: 'M-0004',
    name: '격주(화) 모듈',
    icon: '📋',
    frequency: 'biweekly',
    weekdays: ['TUE'],
    frequencyLabel: '격주 화요일 (홀수주)',
    responseLabel: '해당 주 내 자유 응답',
    responseLabelType: 'primary',
    itemIds: ['insomnia', 'sleep-record'].map(pidByLegacy),
    areaOrder: [],
  },
  {
    mid: 'M-0005',
    name: '매월(화) 모듈',
    icon: '📋',
    frequency: 'monthly',
    weekdays: ['TUE'],
    frequencyLabel: '매월 화요일 (1,5,9,13,17,21,25주)',
    responseLabel: '해당 주 내 자유 응답',
    responseLabelType: 'primary',
    itemIds: [
      'suicide-risk',
      'activity-worse',
      'high-risk-drink',
      'emotional-support',
      'medical-use',
      'emergency-call',
      'welfare-link',
      'resource-network',
      'emergency-coping',
      'caregiver-exists',
    ].map(pidByLegacy),
    areaOrder: [],
  },
  {
    mid: 'M-0006',
    name: '매월(목) 모듈',
    icon: '📋',
    frequency: 'monthly',
    weekdays: ['THU'],
    frequencyLabel: '매월 목요일 (1,5,9,13,17,21,25주)',
    responseLabel: '해당 주 내 자유 응답',
    responseLabelType: 'primary',
    itemIds: ['self-efficacy'].map(pidByLegacy),
    areaOrder: [],
  },
  {
    mid: 'M-0007',
    name: '분기별 모듈',
    icon: '📋',
    frequency: 'quarterly',
    weekdays: [],
    frequencyLabel: '분기별 (13주차)',
    responseLabel: '해당 주 내 자유 응답',
    responseLabelType: 'primary',
    itemIds: ['caregiver-understanding'].map(pidByLegacy),
    areaOrder: [],
  },
];

// ── Seed: disease options (prototype <select> options) ──────────────────────
const diseaseOptions: DiseaseFilterOption[] = [
  { value: 'ALL', label: '전체 (모든 항목)' },
  { value: 'COMMON', label: '공통 항목만' },
  { value: 'GRP:정신건강', label: '정신건강 질병군 전체', group: '정신건강 (F00~F99)' },
  { value: 'F00', label: 'F00 알츠하이머', group: '정신건강 (F00~F99)' },
  { value: 'F10', label: 'F10 알코올 장애', group: '정신건강 (F00~F99)' },
  { value: 'F14', label: 'F14 코카인 장애', group: '정신건강 (F00~F99)' },
  { value: 'F25', label: 'F25 조현정동장애', group: '정신건강 (F00~F99)' },
  { value: 'F32', label: 'F32 우울 에피소드', group: '정신건강 (F00~F99)' },
  { value: 'F41', label: 'F41 기타 불안장애', group: '정신건강 (F00~F99)' },
  { value: 'F43', label: 'F43 스트레스 장애', group: '정신건강 (F00~F99)' },
  { value: 'GRP:순환기', label: '순환기 질병군 전체', group: '순환기 (I00~I99)' },
  { value: 'I10', label: 'I10 본태성 고혈압', group: '순환기 (I00~I99)' },
  { value: 'I63', label: 'I63 뇌경색', group: '순환기 (I00~I99)' },
  { value: 'GRP:내분비', label: '내분비 질병군 전체', group: '내분비 (E00~E99)' },
  { value: 'E11', label: 'E11 제2형 당뇨병', group: '내분비 (E00~E99)' },
];

// ── Page-level state ────────────────────────────────────────────────────────
const pageState = {
  diseaseFilter: 'F32',          // PRD 06 follow-prototype default (Q3).
  monitoringWeeks: 26,
};

const deployState: DeployState = {
  // null = no deploy history yet (first entry → FE shows no chip); flips to
  // 'DEPLOYED' after a successful deploy. The mock has no server-side module-
  // MODIFIED tracking, so the 'CHANGED' chip is driven by the client dirty
  // store — matching the real BE wire shape (null | 'CHANGED' | 'DEPLOYED').
  status: null,
  lastDeployedAt: null,
};

// ── Helpers ─────────────────────────────────────────────────────────────────
function getDiseaseGroupFromCode(code: string): string {
  if (!code) return '';
  if (code.startsWith('F')) return '정신건강';
  if (code.startsWith('I')) return '순환기';
  if (code.startsWith('E')) return '내분비';
  return '';
}

function itemMatchesDisease(item: ProItemRef, filter: string): boolean {
  if (filter === 'ALL') return true;
  if (filter === 'COMMON') return item.scope === 'COMMON';
  if (filter.startsWith('GRP:')) {
    const grp = filter.slice(4);
    if (item.scope === 'COMMON') return true;
    if (item.scope === 'GROUP') return item.diseaseGroup === grp;
    return false;
  }
  // Individual disease code.
  const grp = getDiseaseGroupFromCode(filter);
  if (item.scope === 'COMMON') return true;
  if (item.scope === 'GROUP') return item.diseaseGroup === grp;
  if (item.scope === 'SPECIFIC') return item.diseaseCode === filter;
  return false;
}

function findOwnerMid(pid: string): string | undefined {
  return modules.find((m) => m.itemIds.includes(pid))?.mid;
}

function findModule(mid: string): Module | undefined {
  return modules.find((m) => m.mid === mid);
}

function findItem(pid: string): ProItemRef | undefined {
  return itemPool.find((i) => i.pid === pid);
}

// ── Validation ──────────────────────────────────────────────────────────────
function validateMonitoringWeeks(weeks: number): void {
  if (!Number.isInteger(weeks) || weeks < 1 || weeks > 52) {
    throw new ProDeploymentApiError(
      'MONITOR_WEEKS_OUT_OF_RANGE',
      '모니터링 기간은 1~52주 사이여야 합니다.',
      { fields: { monitoringWeeks: '1~52주 범위로 입력해주세요.' } },
    );
  }
}

function validateDiseaseFilter(filter: string): void {
  if (!filter.trim()) {
    throw new ProDeploymentApiError(
      'DISEASE_FILTER_REQUIRED',
      '적용 질병을 선택해주세요.',
      { fields: { diseaseFilter: '적용 질병을 선택해주세요.' } },
    );
  }
}

// ── History (sessionStorage-backed; mirrors prototype) ──────────────────────
function readHistory(): DeployHistoryEntry[] {
  try {
    const raw = sessionStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as DeployHistoryEntry[]) : [];
  } catch {
    return [];
  }
}

function pushHistory(entry: DeployHistoryEntry): void {
  const list = [entry, ...readHistory()].slice(0, HISTORY_CAP);
  try {
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(list));
  } catch {
    // sessionStorage quota errors are silent — history is non-critical UX.
  }
}

function nowKstString(): string {
  const d = new Date();
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())}` +
    ` ${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// ── API surface ─────────────────────────────────────────────────────────────
export const proDeploymentMockApi: ProDeploymentApi = {
  async getCurrentConfig(query): Promise<ProDeploymentCurrentConfig> {
    await delay();
    if (query?.disease) validateDiseaseFilter(query.disease);
    if (query?.weeks !== undefined) validateMonitoringWeeks(query.weeks);
    if (query?.disease) pageState.diseaseFilter = query.disease;
    if (query?.weeks !== undefined) pageState.monitoringWeeks = query.weeks;
    // The real BE returns the disease-SCOPED module set (ensureModulesForDisease):
    // only modules that belong to the selected disease, and the FE renders them
    // verbatim (no client re-filtering). This mock keeps a single shared module
    // set, so it mirrors that contract here — a module is returned when it is
    // empty (fixed slot, ADM.007) or has ≥1 item in the selected disease scope.
    // `itemIds` are kept intact so edits still operate on the full composition.
    const filter = pageState.diseaseFilter;
    const scopedModules = modules.filter(
      (m) =>
        m.itemIds.length === 0 ||
        m.itemIds.some((pid) => {
          const item = itemPool.find((i) => i.pid === pid);
          return item ? itemMatchesDisease(item, filter) : false;
        }),
    );
    // `items` is the full dictionary — item-level scoping is a BE concern; the FE
    // only needs the dictionary to resolve the (already-scoped) module members.
    return clone({
      diseaseFilter: pageState.diseaseFilter,
      monitoringWeeks: pageState.monitoringWeeks,
      modules: scopedModules,
      items: itemPool,
      deploy: deployState,
    });
  },

  async listDiseaseOptions(): Promise<DiseaseFilterOption[]> {
    await delay(80);
    return clone(diseaseOptions);
  },

  async listItemPool(query: ItemPoolQuery): Promise<ProItemRef[]> {
    await delay(80);
    validateDiseaseFilter(query.disease);
    const result = itemPool.filter((i) => {
      if (i.freqCat !== query.freq) return false;
      if (!itemMatchesDisease(i, query.disease)) return false;
      // ADM.006 — one item ↔ one module: only offer items not yet assigned to
      // ANY module. Items already placed (here or elsewhere) are excluded, so
      // there is no cross-module "move" from the popover.
      return !findOwnerMid(i.pid);
    });
    return clone(result);
  },

  async updatePageConfig(input: UpdatePageConfigInput) {
    await delay();
    if (input.diseaseFilter !== undefined) {
      validateDiseaseFilter(input.diseaseFilter);
      pageState.diseaseFilter = input.diseaseFilter;
    }
    if (input.monitoringWeeks !== undefined) {
      validateMonitoringWeeks(input.monitoringWeeks);
      pageState.monitoringWeeks = input.monitoringWeeks;
    }
    return { ...pageState };
  },

  async updateModule(mid: string, input: UpdateModuleInput): Promise<Module> {
    await delay();
    const target = findModule(mid);
    if (!target) {
      throw new ProDeploymentApiError('MODULE_NOT_FOUND', '모듈을 찾을 수 없습니다.');
    }
    // Validate every incoming PID exists + matches the module's frequency.
    for (const pid of input.itemIds) {
      const item = findItem(pid);
      if (!item) {
        throw new ProDeploymentApiError('ITEM_NOT_FOUND', 'PRO 항목을 찾을 수 없습니다.', {
          fields: { itemId: pid },
        });
      }
      if (item.freqCat !== target.frequency) {
        throw new ProDeploymentApiError(
          'ITEM_FREQ_MISMATCH',
          '해당 모듈의 주기와 일치하지 않는 항목입니다.',
          { fields: { itemId: pid }, meta: { expectedFreq: target.frequency } },
        );
      }
    }
    // ADM.006 / PRD §8 — one item ↔ one module. Reject any incoming PID that
    // already belongs to a different module (no cross-module move). The
    // add-popover only offers unassigned items, so this is a safety net that
    // mirrors the BE's ITEM_ALREADY_PLACED.
    for (const pid of input.itemIds) {
      const ownerMid = findOwnerMid(pid);
      if (ownerMid && ownerMid !== mid) {
        const owner = findModule(ownerMid);
        throw new ProDeploymentApiError(
          'ITEM_ALREADY_PLACED',
          '이미 다른 모듈에 포함된 항목입니다.',
          { meta: { ownerMid, ownerName: owner?.name } },
        );
      }
    }
    // Keep only the areas actually present after the swap — like the server, an
    // area no item holds is dropped from the stored order, not refused.
    const presentAreas = new Set(
      input.itemIds
        .map((pid) => findItem(pid)?.area)
        .filter((a): a is string => Boolean(a)),
    );
    const areaOrder = input.areaOrder.filter((area) => presentAreas.has(area));
    // Like the server, an item that joins through this save brings its area to the END of a stored order; an
    // empty order stays empty, so the screen keeps deriving it.
    const joinedAreas = input.itemIds
      .filter((pid) => !target.itemIds.includes(pid))
      .map((pid) => findItem(pid)?.area)
      .filter((a): a is string => Boolean(a));
    for (const area of areaOrder.length > 0 ? joinedAreas : []) {
      if (!areaOrder.includes(area)) areaOrder.push(area);
    }
    target.itemIds = [...input.itemIds];
    target.areaOrder = areaOrder;
    return clone(target);
  },

  async deploy(input: DeployInput): Promise<DeployResult> {
    await delay();
    // FEATURE_025 — the 전체 row cannot be deployed; the server refuses it the same way.
    if (input.diseaseFilter === 'ALL') {
      throw new ProDeploymentApiError('DISEASE_FILTER_REQUIRED', '적용 질병을 선택해주세요.');
    }
    const deployedAt = nowKstString();
    deployState.status = 'DEPLOYED';
    deployState.lastDeployedAt = deployedAt;
    const entry: DeployHistoryEntry = {
      deployedAt,
      // TODO(auth): once the auth store carries the live user's display name +
      // primary role, pull them from there. For now the prototype-style ROOT
      // fallback keeps the row populated.
      deployerName: '관리자',
      deployerRole: 'ROOT',
      target: diseaseOptions.find((o) => o.value === input.diseaseFilter)?.label ?? input.diseaseFilter,
      summary: { ...input.summary },
    };
    pushHistory(entry);
    // The mock keeps one draft set and no layer publications, so nothing is ever held.
    return { deploy: clone(deployState), history: clone(entry) };
  },

  async listHistory(limit: number = 10): Promise<DeployHistoryEntry[]> {
    await delay(80);
    return readHistory().slice(0, Math.min(limit, HISTORY_CAP));
  },

  // FEATURE_025 — the mock keeps no patient drafts, so a deployable row resets none.
  async getResetCount(disease: string): Promise<number | null> {
    await delay(80);
    return disease === 'ALL' ? null : 0;
  },
};
